import { useCallback, useEffect, useState } from 'react';
import { request } from '../http.ts';

/**
 * What this deployment is doing right now, across every workflow.
 *
 * The Runs tab answers "what has *this workflow* done", caps at fifty, and is
 * the wrong screen for the question somebody actually arrives with when the
 * spend graph moves: which workflow, started by whom, and is it still going.
 * Admin knows the totals — runs in flight, spend over the rolling day — and had
 * nowhere to break them down, so a ceiling that was being approached was a
 * number with no story attached.
 *
 * Fan-out is what made this necessary rather than nice. One `forEach` step over
 * forty repositories is forty step runs under one parent, and a board that
 * could only show runs would report that as a single row costing an
 * unexplained amount.
 */
interface FleetRun {
  id: string;
  workflowId: string;
  workflowName: string;
  status: string;
  triggerType: string;
  triggeredBy: string | null;
  spendCents: number;
  stepCount: number;
  createdAt: string;
  intent: string | null;
}

interface WorkflowSpend {
  workflowId: string;
  workflowName: string;
  runs: number;
  spendCents: number;
}

type Lens = 'live' | 'all' | 'spend';

export function FleetView({ isAdmin }: { isAdmin: boolean }) {
  const [lens, setLens] = useState<Lens>('live');
  const [runs, setRuns] = useState<FleetRun[]>([]);
  const [spend, setSpend] = useState<WorkflowSpend[]>([]);
  const [error, setError] = useState<string | null>(null);
  /*
   * What a control did, kept apart from `error` because `refresh` clears the
   * error on every poll — and the poll runs immediately after a control lands.
   * Sharing one slot meant the sentence explaining that a cancel is still in
   * progress was wiped before anybody could read it.
   */
  const [effect, setEffect] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      request<{ runs: FleetRun[] }>('/fleet/runs'),
      request<{ spend: WorkflowSpend[] }>('/fleet/spend'),
    ])
      .then(([runResult, spendResult]) => {
        setRuns(runResult.runs);
        setSpend(spendResult.spend);
      })
      .catch((caught) => setError((caught as Error).message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(refresh, [refresh]);

  /*
   * Polled rather than streamed. A board showing every run in the deployment
   * would need a stream per run to be live, and the thing it is watching for —
   * a run appearing, a status changing — moves on the order of seconds. The
   * per-run timeline is where live matters.
   */
  useEffect(() => {
    const timer = setInterval(refresh, 10_000);
    return () => clearInterval(timer);
  }, [refresh]);

  const control = async (runId: string, intent: 'cancel' | 'pause' | 'resume') => {
    setBusy(`${runId}:${intent}`);
    setError(null);
    setEffect(null);
    try {
      const body = await request<{ effect?: string }>(
        `/runs/${encodeURIComponent(runId)}/control`,
        { method: 'POST', body: JSON.stringify({ intent }) },
      );
      setEffect(body.effect ?? null);
      refresh();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const live = runs.filter((run) => LIVE.has(run.status));
  const shown = lens === 'live' ? live : runs;
  const totalSpend = spend.reduce((sum, entry) => sum + entry.spendCents, 0);

  return (
    <div className="body">
      <aside className="sidebar">
        <p className="section-title">Fleet</p>
        <button className="list-item" aria-current={lens === 'live'} onClick={() => setLens('live')}>
          In flight
          <small>{live.length} running now</small>
        </button>
        <button className="list-item" aria-current={lens === 'all'} onClick={() => setLens('all')}>
          Everything recent
          <small>{runs.length} runs</small>
        </button>
        <button className="list-item" aria-current={lens === 'spend'} onClick={() => setLens('spend')}>
          Spend by workflow
          <small>${(totalSpend / 100).toFixed(2)} in 24h</small>
        </button>
      </aside>

      <main className="main">
        <h1 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem' }}>
          {lens === 'spend' ? 'Spend by workflow' : 'Runs across the deployment'}
        </h1>
        <p className="hint" style={{ marginTop: 0 }}>
          {lens === 'spend'
            ? 'The rolling 24 hours, attributed to the workflow that spent it. The ceilings screen has the total; this is what makes it actionable.'
            : 'Every workflow, not just one. A run started by a schedule or a webhook has nobody to ask, so this is where somebody notices it is still going.'}
        </p>

        {effect && (
          <div className="banner warn">
            <p style={{ margin: 0 }}>{effect}</p>
          </div>
        )}

        {error && (
          <div className="banner danger">
            <p style={{ margin: 0 }}>{error}</p>
          </div>
        )}

        {lens === 'spend' ? (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table>
              <thead>
                <tr>
                  <th>Workflow</th>
                  <th>Runs</th>
                  <th>Spend</th>
                </tr>
              </thead>
              <tbody>
                {spend.map((entry) => (
                  <tr key={entry.workflowId}>
                    <td>
                      <strong>{entry.workflowName}</strong>
                      <div className="hint">{entry.workflowId}</div>
                    </td>
                    <td>{entry.runs}</td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                      ${(entry.spendCents / 100).toFixed(2)}
                    </td>
                  </tr>
                ))}
                {spend.length === 0 && (
                  <tr>
                    <td colSpan={3} className="hint">
                      Nothing has run in the last 24 hours.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table>
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Workflow</th>
                  <th>Started by</th>
                  <th>Steps</th>
                  <th>Spend</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.map((run) => (
                  <tr key={run.id}>
                    <td>
                      <span className={`pill ${tone(run.status)}`}>{run.status}</span>
                      {/*
                        Only a pending cancel earns a second chip. Pausing is
                        already the run's status, so a chip beside it would say
                        the same thing twice — but a cancel that has been asked
                        for and not yet reached is a state the status cannot
                        express: "cancelling" is honest where "cancelled" would
                        claim that nothing is still writing.
                      */}
                      {run.intent === 'cancel' && run.status !== 'cancelled' && (
                        <span className="pill warn">cancelling</span>
                      )}
                      <div className="hint">
                        <code>{run.id}</code>
                      </div>
                    </td>
                    <td>{run.workflowName}</td>
                    <td className="hint">{run.triggerType}</td>
                    <td>{run.stepCount}</td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                      ${(run.spendCents / 100).toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {LIVE.has(run.status) && (
                        <span className="row" style={{ justifyContent: 'flex-end' }}>
                          {run.status === 'paused' ? (
                            <button
                              className="ghost"
                              disabled={busy !== null}
                              onClick={() => void control(run.id, 'resume')}
                            >
                              Resume
                            </button>
                          ) : (
                            <button
                              className="ghost"
                              disabled={busy !== null}
                              onClick={() => void control(run.id, 'pause')}
                            >
                              Pause
                            </button>
                          )}
                          <button
                            className="ghost danger"
                            disabled={busy !== null || run.intent === 'cancel'}
                            onClick={() => void control(run.id, 'cancel')}
                          >
                            Cancel
                          </button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={6} className="hint">
                      {lens === 'live' ? 'Nothing is running right now.' : 'No runs yet.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="row">
          <button onClick={refresh} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          {!isAdmin && (
            <span className="hint">
              You can see every run and control the ones on workflows you can edit.
            </span>
          )}
        </div>
      </main>
    </div>
  );
}

const LIVE = new Set(['pending', 'running', 'awaiting_approval', 'paused']);

function tone(status: string): string {
  if (status === 'completed') return 'ok';
  if (status === 'failed' || status === 'cancelled') return 'danger';
  if (status === 'running') return 'accent';
  return 'warn';
}
